import { useEffect, useState } from "react";
import { CalendarClock, Eye, LogIn, RefreshCw } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import usePageMeta from "@/hooks/usePageMeta";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminAnalytics() {
  usePageMeta(
    "Analytics — Transactr",
    "Internal analytics: page views and demo conversions across the Transactr site. First-party, no cookies."
  );
  const [summary, setSummary] = useState(null);
  const [series, setSeries] = useState(null);

  const load = () => {
    fetch(`${API}/api/analytics/summary`)
      .then((r) => r.json())
      .then(setSummary)
      .catch(() => setSummary({ views: [], events: [] }));
    fetch(`${API}/api/analytics/timeseries?days=14`)
      .then((r) => r.json())
      .then(setSeries)
      .catch(() => setSeries({ series: [] }));
  };
  useEffect(load, []);

  const totalViews = summary ? summary.views.reduce((t, v) => t + v.count, 0) : 0;
  const ev = (n) =>
    summary && summary.events.find((e) => e.name === n)
      ? summary.events.find((e) => e.name === n).count
      : 0;
  const maxPath = summary && summary.views.length ? Math.max(...summary.views.map((v) => v.count)) : 0;
  const days = series ? series.series : [];
  const maxDay = days.length ? Math.max(...days.map((d) => d.views + d.sign_in_clicks + d.book_demo_clicks), 1) : 1;

  return (
    <section className="container-t pb-24 pt-32 lg:pt-40" data-testid="admin-analytics-page">
      <Reveal>
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="eyebrow">
              <span className="eyebrow-mark" />
              Internal
            </p>
            <h1 className="mt-5 font-display text-4xl font-extrabold tracking-[-0.02em] text-ink lg:text-5xl">
              Analytics
            </h1>
            <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.18em] text-inksoft">
              First-party · no cookies · Do Not Track respected
            </p>
          </div>
          <button
            data-testid="admin-refresh-btn"
            onClick={load}
            className="group inline-flex items-center gap-2 rounded-full border border-ink/20 px-5 py-2.5 text-sm font-semibold text-ink transition-all duration-300 hover:border-ink hover:bg-ink hover:text-paper"
          >
            <RefreshCw size={14} className="transition-transform duration-500 group-hover:rotate-180" />
            Refresh
          </button>
        </div>
        <div className="mt-8 h-px w-24 bg-bluerule" />
      </Reveal>

      {/* STAT CARDS */}
      <div className="mt-12 grid gap-5 sm:grid-cols-3">
        {[
          { icon: Eye, label: "Page views", value: totalViews, testid: "stat-views" },
          { icon: LogIn, label: "Sign-in clicks", value: ev("sign_in_click"), testid: "stat-signin" },
          { icon: CalendarClock, label: "Book-a-demo clicks", value: ev("book_demo_click"), testid: "stat-bookdemo" },
        ].map((s, i) => (
          <Reveal key={s.label} delay={i * 0.08}>
            <div className="rounded-3xl border border-line bg-white p-8" data-testid={s.testid}>
              <div className="flex items-center justify-between">
                <s.icon size={20} strokeWidth={1.6} className="text-vermillion" />
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-inksoft">
                  {s.label}
                </span>
              </div>
              <p className="mt-6 font-display text-5xl font-extrabold tracking-tight text-ink">
                {s.value}
              </p>
            </div>
          </Reveal>
        ))}
      </div>

      {/* VIEWS BY PAGE */}
      <Reveal delay={0.1} className="mt-14 block">
        <div className="rounded-3xl border border-line bg-white p-8 lg:p-10" data-testid="views-by-page">
          <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-ink">
            Views by page
          </p>
          {summary && summary.views.length === 0 ? (
            <p className="mt-6 text-sm text-inksoft">
              No data yet — visit some pages, then refresh.
            </p>
          ) : (
            <ul className="mt-7">
              {(summary ? summary.views : []).map((v) => (
                <li key={v.path} className="border-t border-line py-4 first:border-0 first:pt-0">
                  <div className="flex items-center justify-between gap-4 text-sm">
                    <span className="font-mono text-xs text-ink">{v.path}</span>
                    <span className="font-display font-bold text-ink">{v.count}</span>
                  </div>
                  <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-parchment">
                    <div
                      className="h-full rounded-full bg-vermillion"
                      style={{ width: `${Math.max((v.count / maxPath) * 100, 4)}%` }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Reveal>

      {/* 14-DAY CHART */}
      <Reveal delay={0.15} className="mt-6 block">
        <div className="rounded-3xl border border-line bg-white p-8 lg:p-10" data-testid="timeseries-chart">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-ink">
              Last 14 days
            </p>
            <div className="flex gap-5">
              {[
                ["bg-bluerule", "Views"],
                ["bg-vermillion", "Sign-in"],
                ["bg-bluespark", "Book a demo"],
              ].map(([c, l]) => (
                <span key={l} className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.14em] text-inksoft">
                  <span className={`h-2 w-2 rounded-sm ${c}`} />
                  {l}
                </span>
              ))}
            </div>
          </div>
          {days.length === 0 ? (
            <p className="mt-6 text-sm text-inksoft">No data in the last 14 days yet.</p>
          ) : (
            <div className="mt-8 flex h-44 items-end gap-1.5">
              {days.map((d) => (
                <div key={d.day} className="flex h-full flex-1 flex-col justify-end gap-0.5" title={`${d.day}: ${d.views} views, ${d.sign_in_clicks} sign-in, ${d.book_demo_clicks} book-a-demo`}>
                  <div className="flex w-full flex-col justify-end gap-0.5" style={{ height: `${((d.views + d.sign_in_clicks + d.book_demo_clicks) / maxDay) * 100}%` }}>
                    {d.book_demo_clicks > 0 ? (
                      <div className="w-full rounded-t-sm bg-bluespark" style={{ height: `${(d.book_demo_clicks / maxDay) * 100}%` }} />
                    ) : null}
                    {d.sign_in_clicks > 0 ? (
                      <div className="w-full rounded-t-sm bg-vermillion" style={{ height: `${(d.sign_in_clicks / maxDay) * 100}%` }} />
                    ) : null}
                    <div className="w-full rounded-t-sm bg-bluerule" style={{ height: `${(d.views / maxDay) * 100}%`, minHeight: 2 }} />
                  </div>
                  <span className="mt-1.5 block text-center font-mono text-[9px] text-inksoft/60">
                    {d.day.slice(8)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </Reveal>
    </section>
  );
}
